import { Box, Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';

import { useValidationExitGuard } from '../../hooks/useValidationExitGuard';
import { ActionComponentProps } from '../../index';
import { buildValidationPieces } from '../../utils';
import useNotifyComment from './NotifyComment/useNotifyComment';
import NotifyMairoomComment from './NotifyComment';

export const FinishPickUp = ({ mailpieces, actionStarted, onActionFinish, validationData }: ActionComponentProps) => {
  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, onActionFinish);

  const [updatePieces] = useUpdatePiecesMutation();

  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const { values, changeValues } = useNotifyComment();

  const confirmAction = async () => {
    try {
      setActionInProgress(true);

      await updatePieces({
        pieces: buildValidationPieces(mailpieces, validationData),
        action: PieceAction.FinishPickUp,
        shouldNotifyRecipient: !values.skipNotifyRecipient,
      }).unwrap();

      const comment = values.comment.trim();

      if (comment.length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom: values.notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      setActionInProgress(false);
      onActionFinish();
    } catch (error) {
      setActionInProgress(false);
    }
  };

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary">
        {mailpieces.length === 1 ? (
          <>Please give this item to the recipient, and click Complete when you&apos;re done.</>
        ) : (
          <>
            Please give <strong>{mailpieces.length}</strong> items to the recipient, and click Complete when you&apos;re
            done.
          </>
        )}
      </Typography>

      <NotifyMairoomComment values={values} onChange={changeValues} />
    </Box>
  );

  return (
    <>
      <DialogConfirmation
        open={actionStarted}
        loading={actionInProgress}
        maxWidth="sm"
        title={mailpieces.length === 1 ? 'Complete pickup' : 'Complete pickup for selected items'}
        content={content}
        acceptButtonHandleClick={confirmAction}
        cancelButtonHandleClick={guardedExit}
        acceptButtonTitle="Complete"
        cancelButtonTitle="Cancel"
      />

      {exitConfirmDialog}
    </>
  );
};
